<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>

        <b-modal
            v-model="modalShowing"
            title="Add Company Location"
            centered
            content-class="bg-gray-100"
            hide-footer
            @show="onModalShow"
            @hidden="onModalHidden"
        >
            <div v-if="editMode">

                <b-overlay :show="loadingSave">

                    <template #overlay>
                        <div v-if="saveSuccess" key="saveSuccess" class="text-center animate__animated animate__bounceIn">
                            <b-icon icon="check-circle" variant="success" font-scale="3"></b-icon>
                            <div class="mt-3 h5 text-center">Address Saved</div>
                        </div>
                        <b-spinner v-else key="savingSpinner"></b-spinner>
                    </template>

                    <formulate-form
                        v-model="addressObj"
                        ref="addressForm"
                        @input="handleFormInput"
                        @submit="handleSubmit"
                    >

                        <formulate-input
                            name="name"
                            label="Location Name"
                            validation="required"
                            validation-name="Location Name"
                        ></formulate-input>

                        <hr>

                        <div class="d-flex w-100">

                            <formulate-input
                                name="user_addr1"
                                label="Street Address"
                                validation-name="Address 1"
                                :outer-class="['flex-fill', 'w-65', 'pr-2', 'mb-2']"
                                :element-class="['fill']"
                            ></formulate-input>

                            <formulate-input
                                name="user_subpremise"
                                label="Floor / Suite / Unit"
                                validation-name="Floor / Suite / Unit"
                                :outer-class="['flex-fill', 'w-35', 'mb-2']"
                                :element-class="['fill']"
                            ></formulate-input>

                        </div>
                        
                        <formulate-input
                            name="user_addr2"
                            label="Street Address 2"
                            :outer-class="['mb-2']"
                        ></formulate-input>

                        <div class="d-flex w-100">

                            <formulate-input
                                name="user_city"
                                label="City"
                                validation-name="City"
                                :outer-class="['flex-fill', 'w-40', 'pr-2', 'mb-2']"
                            ></formulate-input>

                            <formulate-input
                                name="user_state"
                                label="State / Province"
                                validation-name="State or Province"
                                :outer-class="['flex-fill', 'w-30', 'pr-2', 'mb-2']"
                            ></formulate-input>

                            <formulate-input
                                name="user_postcode"
                                label="Postal Code"
                                validation-name="Postal Code"
                                :outer-class="['flex-fill', 'w-30', 'mb-2']"
                            ></formulate-input>

                        </div>

                        <formulate-input
                            name="user_country_code"
                            type="select"
                            label="Country"
                            :options="countriesOptionsFormulate"
                            placeholder="Select a country..."
                            validation="required"
                            validation-name="Country"
                            :outer-class="['max-width-250 d-inline-block mr-3']"
                        ></formulate-input>

                        <country-flag v-if="selectedCountryCode" :country="selectedCountryCode"></country-flag>

                        <hr>

                        <div class="phone-number-fields d-flex">

                            <formulate-input
                                name="phone_number"
                                type="phonenumber"
                                :country-code="selectedCountryCode"
                                label="Phone Number"
                                validation-name="Phone number"
                                :outer-class="['w-40', 'mb-2', 'phone-number']"
                            ></formulate-input>

                            <formulate-input
                                name="phone_extension"
                                type="text"
                                label="Extension"
                                :outer-class="['w-20', 'mb-2', 'phone-extension']"
                            ></formulate-input>

                            <!-- <formulate-input
                                name="phone_type"
                                type="text"
                                label="Number Type"
                                value="Main"
                                :outer-class="['w-30', 'mb-2', 'pl-2', 'phone-type']"
                            ></formulate-input> -->

                        </div>
                        
                        <formulate-input
                            type="submit"
                            label="Save Address"
                            :disabled="hasErrors"
                        ></formulate-input>
                    
                    </formulate-form>

                </b-overlay>
            
            </div>
        </b-modal>
        
    </div>
</template>

<script>
// was using abstract/base address form, then stopped - abstract it later
import { errorHandling } from '../js/custom-vue-mixins';
import { suppliosApi } from '../js/api-service';
import cloneDeep from 'lodash/cloneDeep';
//import omit from 'lodash/omit';
import merge from 'lodash/merge';

export default {
    name: 'CompanyLocationModal',
    components: {
        // use base or abstract form component later,
    },
    mixins: [errorHandling],  // this gives you errorToaster(error, content)
    props: {
        companyId: {
            type: [String, Number],
            required: true,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loadingCountries: false, // busy flag for loading countries
            loadingSave: false, // when we are saving address to backend
            modalShowing: false,  // v-model to show/hide modal
            editMode: false,
            saveSuccess: false,  // flag to show success message
            updateId: null,  // if updating an existing address, set this to the ID of update address (maybe no longer needed after switching to @action on Company viewset)
            isUpdate: false,  // set during startCreate and startUpdate
            addressObj: {},  // v-model with address form
            hasErrors: true, // flat we set when checking vue-formulate inputs -- start with true, set to false if no errors found
            countriesOptions: [],
        }
    },
    computed: {
        countriesOptionsFormulate: function() {
            // remap our country options into format expected by vue-formulate select (array of objects with label and value keys)
            if (!this.countriesOptions || !this.countriesOptions.length) return [];
            return this.countriesOptions.map(country => {
                let countryFormulate = {};
                countryFormulate['label'] = country.name;
                countryFormulate['value'] = country.code;
                return countryFormulate;
            });
        },
        selectedCountryCode() {
            // used to be in data, and you set when input changed... but computed seems much better
            return this.addressObj?.user_country_code ?? '';
        },
    },
    methods: {
        startCreate: function() {
            // can call this from parent -- sets blank address, sets editMode, shows modal
            this.addressObj = {};  // make sure its reset
            this.editMode = true;
            this.updateId = null;  // just to make sure nothing was left over -- maybe this is no longer needed
            this.isUpdate = false;  
            this.modalShowing = true;
        },
        startUpdate: function(locationObj) {
            // object to update will get passed in from list component parent -- the full locationObject
            // set the addressObj, then open modal, maybe set something to 'update mode'?
            this.addressObj = cloneDeep(locationObj);  
            this.editMode = true;
            this.updateId = locationObj.id;  // maybe no longer needed
            this.isUpdate = true;
            this.modalShowing = true;
        },
        // above things can be called by parent
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        onModalShow: function() {
            // things we always want to do when first showing address form
            this.getCountriesApi();
        },
        onModalHidden: function() {
            // you can put things here, or put them in startUpdate, startCreate
            this.saveSuccess = false;
            this.loadingSave = false;
        },
        getCountriesApi: function() {
            this.loadingCountries = true;
            return suppliosApi.locations.loadAddressFormData()
                .then(response => {
                    this.countriesOptions = response.data.countries;
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while getting list of countries');
                    return false;
                })
                .then(status => {
                    this.loadingCountries = false;
                    return status;
                });
        },
        handleFormInput: function() {  // called anytime the form values are updated, via @input event on vue-formulate form
            this.performAddressValidation().then(() => {
                // all this stuff below probably isn't needed now that you integrated form into modal... but keeping it for reference
                let fullAddressObject = Object.assign({}, this.addressObj);  // copy the address
                fullAddressObject['hasErrors'] = this.hasErrors;  // add in the current status of errors
                // now emit it to parent as input event
                this.$emit('input', fullAddressObject);
            });
        },
        async performAddressValidation() {
            if (!this.$refs.addressForm) return Promise.resolve(null);  // just do something to block calling .hasValidationErrors() until we have thing to call it on
            this.hasErrors = await this.$refs.addressForm.hasValidationErrors();
        },
        buildDataForApi: function(addressData) {
            // build the object we'll post to the API 
            //if (!this.parentId) return addressData;
            let parentData = {
                parent: this.companyId.toString(),
            };
            // if you're using 'group' input for phone number, also need to take out phone number stuff from array of objects, into object nested directly in main object (because in vue-formulate, all 'group' fields are nested in an array because of repeatables)
            if (addressData.phone_number_group && addressData.phone_number_group.length) {
                let firstPhoneGroup = addressData.phone_number_group[0]
                addressData['phone_number'] = firstPhoneGroup.phone_number;
                addressData['phone_extension'] = firstPhoneGroup.phone_extension;
                addressData['phone_type'] = firstPhoneGroup.phone_type;
            }
            return merge({}, addressData, parentData);  // if we have parentId provided by parent, merge that in
        },
        closeModalDelay: function(delayTime) {
            setTimeout(() => {
                this.modalShowing = false;  // close the modal... all success message stuff is handled separately
            }, delayTime);
        },
        handleSubmit: function(addressData) {
            // this gets called no matter if its an update or create... so figure it out then call the right one
            let clonedAddress = cloneDeep(addressData); // do this right away so we don't mutate the original vue-formulate model
            let dataToPost = this.buildDataForApi(clonedAddress);  // we'll use this no matter if create or update
            if (this.isUpdate) {
                // return this.performUpdate(this.updateId, dataToPost);  // old way, using CompanyLocation viewset
                return this.performUpdate(dataToPost);
            }
            return this.performCreate(dataToPost);  // otherwise, if not update, do the create
        },
        performUpdate: function(dataToPost) {  // previously we were also passing in this.updateId
            this.loadingSave = true;
            //return suppliosApi.companyLocation.update(updateId, dataToPost)
            return suppliosApi.vendorStandard.updateLocation(this.companyId, dataToPost)
                .then(response => {
                    this.saveSuccess = true;
                    this.$emit('updated', response.data);
                    this.closeModalDelay(2000);
                    return true;
                })
                .catch(error => {
                    this.loadingSave = false;  // turn off the overlay so user can deal with errors
                    this.handleError(error, 'Error while updating the address');
                    return false;
                })
                .then(status => {
                    return status;
                });
        },
        performCreate: function(dataToPost) {
            this.loadingSave = true;
            //return suppliosApi.companyLocation.create(dataToPost)  // either of these will work - easier to enforce perms on company viewset @action
            return suppliosApi.vendorStandard.createLocation(this.companyId, dataToPost)
                .then(response => {
                    this.saveSuccess = true;
                    this.$emit('created', response.data);  // emit the new address (with all the new data from backend) up to parent -- maybe change later?
                    this.closeModalDelay(2000);
                    return true;
                })
                .catch(error => {
                    this.loadingSave = false;  // turn off the overlay so user can deal with errors
                    this.handleError(error, 'Error while saving the address');
                    return false;
                })
                .then(status => {
                    //this.loadingSave = false;
                    return status;
                });
        },
        /* updateSelectedCountry: function(countryCode) {
            // countryCode will be the value ('US')
            // console.log('From updateSelectedCountry, countryCode is:');
            console.log(countryCode);
        } */
    }
}
</script>

<style lang="scss">

.phone-number-fields {
    .phone-number input {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        border-right-color: var(--gray-100);
    }
    .phone-extension input {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        border-left-color: var(--gray-100);
    }
}

</style>